package com.rylcart.cp.ui.api

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

class CpApi(private val baseUrl: String, private val authProvider: () -> String?) {

    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    private val http = HttpClient(Js) {
        install(ContentNegotiation) { json(json) }
        defaultRequest {
            contentType(ContentType.Application.Json)
            authProvider()?.let { header(HttpHeaders.Authorization, "Bearer $it") }
        }
        // Don't blow up the coroutine on non-2xx — we want to surface server messages.
        expectSuccess = false
    }

    suspend fun setupStatus(): SetupStatus =
        http.get("$baseUrl/v1/admin/setup-status").require().body()

    suspend fun login(email: String, password: String): TokenResponse =
        http.post("$baseUrl/v1/admin/login") { setBody(LoginRequest(email, password)) }
            .require().body()

    suspend fun register(email: String, password: String, name: String?): TokenResponse =
        http.post("$baseUrl/v1/admin/register") { setBody(RegisterRequest(email, password, name)) }
            .require().body()

    suspend fun tenants(): List<Tenant> = http.get("$baseUrl/v1/tenants").require().body()

    suspend fun tenant(id: String): Tenant = http.get("$baseUrl/v1/tenants/$id").require().body()

    suspend fun createTenant(req: CreateTenantRequest): CreateTenantResponse =
        http.post("$baseUrl/v1/tenants") { setBody(req) }.require().body()

    suspend fun destroyTenant(id: String): RunIdResponse =
        http.delete("$baseUrl/v1/tenants/$id").require().body()

    suspend fun runs(tenantId: String): List<ProvisioningRun> =
        http.get("$baseUrl/v1/tenants/$tenantId/runs").require().body()

    private suspend fun io.ktor.client.statement.HttpResponse.require(): io.ktor.client.statement.HttpResponse {
        if (status.value in 200..299) return this
        val body = runCatching { bodyAsText() }.getOrNull().orEmpty()
        val msg = runCatching { json.parseToJsonElement(body).jsonObject["error"]?.jsonPrimitive?.content }.getOrNull()
        throw ApiException(status.value, msg ?: body.ifBlank { "HTTP $status" })
    }
}

class ApiException(val httpStatus: Int, message: String) : RuntimeException(message)

object AuthStorage {
    private const val KEY = "rylcart_cp_token"
    fun get(): String? = window.localStorage.getItem(KEY)?.takeIf { it.isNotBlank() }
    fun set(token: String) = window.localStorage.setItem(KEY, token)
    fun clear() = window.localStorage.removeItem(KEY)
}
